import React, { useState, useEffect, lazy, Suspense } from "react";
import { Phone, ArrowRight, Facebook, Instagram, Youtube, MapPin } from "lucide-react";
import { WhatsAppIcon, TikTokIcon } from "@/components/SocialIcons";
import { CONTACT, SOCIALS } from "@/config";
import { useI18n } from "@/i18n";

const TechEcosystem = lazy(() =>
  import("@/components/TechEcosystem").then((m) => ({ default: m.TechEcosystem }))
);

const socialHref = Object.fromEntries(SOCIALS.map((s) => [s.icon, s.href]));
const SECONDARY = [
  { key: "whatsapp", label: "WhatsApp", href: socialHref.whatsapp, Icon: WhatsAppIcon, color: "#25D366" },
  { key: "facebook", label: "Facebook", href: socialHref.facebook, Icon: Facebook, color: "#1877F2" },
  { key: "instagram", label: "Instagram", href: socialHref.instagram, Icon: Instagram, color: "#E4405F" },
  { key: "youtube", label: "YouTube", href: socialHref.youtube, Icon: Youtube, color: "#FF0000" },
  { key: "tiktok", label: "TikTok", href: socialHref.tiktok, Icon: TikTokIcon, color: "#111827" },
  { key: "maps", label: "Mapy", href: CONTACT.directionsHref, Icon: MapPin, color: "#34A853" },
];

export const Hero = () => {
  const { t } = useI18n();
  const [scrollEnabled, setScrollEnabled] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setScrollEnabled(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const go = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="home" className="relative bg-white lg:h-[220vh]">
      <div className="relative lg:sticky lg:top-0 min-h-screen lg:h-screen overflow-hidden tech-grid">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand/10 blur-3xl" />
        <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-navy/5 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen lg:h-screen grid lg:grid-cols-2 gap-12 items-center pt-28 pb-16">
          <div className="z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-navy/5 border border-navy/10 mb-6 max-w-full">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.18em] font-bold text-navy/70">{t.hero.badge}</span>
            </div>

            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-navy leading-[1.08]">
              {t.hero.titleA}
              <span className="text-brand">{t.hero.titleB}</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="hero-book-btn"
                onClick={() => go("#booking")}
                className="btn-lift inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3.5 rounded-md shadow-lg shadow-brand/25"
              >
                {t.hero.book} <ArrowRight className="w-4 h-4" />
              </button>
              <a
                data-testid="hero-call-btn"
                href={CONTACT.phoneHref}
                className="btn-lift inline-flex items-center gap-2 bg-white border-2 border-navy/15 hover:border-navy text-navy font-semibold px-6 py-3.5 rounded-md"
              >
                <Phone className="w-4 h-4" /> {t.hero.call}
              </a>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2.5" data-testid="hero-social-row">
              {SECONDARY.map(({ key, label, href, Icon, color }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  data-testid={`hero-social-${key}`}
                  style={{ "--icon-color": color }}
                  className="hero-social-btn group w-11 h-11 rounded-lg border border-navy/10 bg-white flex items-center justify-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                >
                  <Icon className="w-5 h-5 text-navy/70 transition-colors group-hover:text-[color:var(--icon-color)]" />
                </a>
              ))}
            </div>

            {scrollEnabled && (
              <p className="mt-8 text-xs text-slate-500 flex items-center gap-2">
                <span className="animate-bounce">↓</span> {t.hero.scrollHint}
              </p>
            )}
          </div>

          <div className="relative flex items-center justify-center">
            <Suspense fallback={<div className="w-full max-w-[560px] mx-auto aspect-square" />}>
              <TechEcosystem labels={t.ecosystem} />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
};
